import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import ModuleHeader from "@/components/voff/ModuleHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Cloud, Wind, Waves } from "lucide-react";
import { toast } from "sonner";

const WeatherRouting = () => {
  const [running, setRunning] = useState(false);
  const [brief, setBrief] = useState<any>(null);
  const [route, setRoute] = useState("Georgetown → Liza Destiny");

  const runBrief = () => {
    setRunning(true);
    setTimeout(() => {
      setBrief({
        verdict: "GO WITH CAUTION", color: "bg-[#E8A020]",
        windowDeparture: "0500-0900 LT", windowReturn: "1400-1800 LT",
        forecast: { hs: 1.4, wind: 18, swell: "NE 1.2m @ 9s", visibility: "10nm+" },
        narrative: "Forecast conditions are within AIRCAT 35 SES operating envelope (Hs 1.4m vs limit 2.0m; wind 18kt vs limit 30kt). Expect reduced air-cushion efficiency above Hs 1.5m — fuel burn ~12% above plan. Departure window 0500-0900 LT recommended to clear FPSO arrival prior to afternoon swell pickup. NE trade swell gives following sea on outbound leg, beam on return — passengers may experience moderate motion. Squall risk PM — return window closes by 1800 LT.",
        breakdown: [
          { phase: "Departure (Georgetown)", hs: 0.8, wind: 12, status: "GO" },
          { phase: "Mid-passage (90nm offshore)", hs: 1.4, wind: 18, status: "GO" },
          { phase: "FPSO approach (last 20nm)", hs: 1.6, wind: 22, status: "CAUTION" },
          { phase: "Return — afternoon", hs: 1.7, wind: 20, status: "CAUTION" },
        ],
        risks: ["PM squall line forecast — 30% probability of brief winds 30+kt", "Limited visibility post-1900 LT (sunset)", "Currents on FPSO approach may set vessel onto installation"],
      });
      setRunning(false);
      toast.success("Weather brief generated");
    }, 1800);
  };

  return (
    <>
      <ModuleHeader title="Weather Routing Brief" subtitle="AI-Powered Operational Weather Recommendations" accent="lime" />

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2 border-[#D0DDE6]">
          <CardContent className="p-6 space-y-3">
            <h3 className="font-bold text-[#00283C]">Route Configuration</h3>
            <div><Label className="text-xs">Route</Label><Select value={route} onValueChange={setRoute}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Georgetown → Liza Destiny","Georgetown → Prosperity FPSO","Luanda → Block 15","Port Harcourt → Erha FPSO","Port Harcourt → Usan"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Vessel</Label><Select defaultValue="Velocity Vanguard"><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Velocity Vanguard","Velocity Cobalto","Velocity Paladio"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs">Date</Label><Input type="date" /></div><div><Label className="text-xs">Crew change</Label><Input placeholder="Yes/No" /></div></div>
            <div><Label className="text-xs">Operating Window</Label><Input placeholder="06:00-18:00 LT" /></div>
            <div className="bg-[#F4F7FA] p-3 rounded text-xs space-y-1">
              <div className="font-bold text-[#00283C]">SES Operating Envelope:</div>
              <div>Max Hs: <span className="font-mono">2.0m</span></div>
              <div>Max wind: <span className="font-mono">30 kt</span></div>
              <div>Reduced efficiency: <span className="font-mono">Hs &gt; 1.5m</span></div>
            </div>
            <Button onClick={runBrief} disabled={running} className="w-full bg-[#8CB43C] hover:bg-[#8CB43C]/90 text-white">
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analysing...</> : "Generate Weather Brief"}
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 border-[#D0DDE6]">
          <CardContent className="p-6">
            {!brief ? (
              <div className="text-center py-20 text-muted-foreground"><Cloud className="w-16 h-16 mx-auto mb-4 text-[#288CC8]/40" /><p>Run brief to view operational recommendation</p></div>
            ) : (
              <div className="space-y-4">
                <div className={`${brief.color} text-white p-5 rounded-lg`}>
                  <div className="text-xs uppercase tracking-widest opacity-80">Operational Recommendation</div>
                  <div className="text-3xl font-bold mt-1">{brief.verdict}</div>
                  <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                    <div><div className="text-xs opacity-70">Departure window</div><div className="font-bold">{brief.windowDeparture}</div></div>
                    <div><div className="text-xs opacity-70">Return window</div><div className="font-bold">{brief.windowReturn}</div></div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-[#F4F7FA] p-3 rounded"><Waves className="w-4 h-4 mx-auto text-[#288CC8] mb-1" /><div className="text-[10px] uppercase text-[#6B8A9A]">Hs</div><div className="font-bold text-sm">{brief.forecast.hs}m</div></div>
                  <div className="bg-[#F4F7FA] p-3 rounded"><Wind className="w-4 h-4 mx-auto text-[#288CC8] mb-1" /><div className="text-[10px] uppercase text-[#6B8A9A]">Wind</div><div className="font-bold text-sm">{brief.forecast.wind}kt</div></div>
                  <div className="bg-[#F4F7FA] p-3 rounded"><div className="text-[10px] uppercase text-[#6B8A9A] mb-1">Swell</div><div className="font-bold text-sm">{brief.forecast.swell}</div></div>
                  <div className="bg-[#F4F7FA] p-3 rounded"><div className="text-[10px] uppercase text-[#6B8A9A] mb-1">Visibility</div><div className="font-bold text-sm">{brief.forecast.visibility}</div></div>
                </div>
                <div className="bg-[#F4F7FA] p-4 rounded">
                  <div className="text-xs font-bold uppercase text-[#288CC8] mb-2">AI Narrative</div>
                  <p className="text-sm text-[#0D1E2B] leading-relaxed">{brief.narrative}</p>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-[#288CC8] mb-2">Voyage Phase Breakdown</div>
                  {brief.breakdown.map((b: any) => (
                    <div key={b.phase} className="flex justify-between items-center py-2 border-b border-[#D0DDE6]/50 text-sm">
                      <span>{b.phase}</span>
                      <span className="text-xs text-[#6B8A9A]">Hs {b.hs}m • {b.wind}kt</span>
                      <Badge className={b.status === "GO" ? "bg-[#2CA048] text-white" : b.status === "CAUTION" ? "bg-[#E8A020] text-white" : "bg-[#C83228] text-white"}>{b.status}</Badge>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-[#288CC8] mb-2">Risks</div>
                  <ul className="text-sm space-y-1">{brief.risks.map((r: string) => <li key={r}>• {r}</li>)}</ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export const WeatherRoutingContent = WeatherRouting;

const WeatherRoutingPage = () => (
  <IntranetLayout>
    <WeatherRoutingContent />
  </IntranetLayout>
);

export default WeatherRoutingPage;
